import { z } from 'zod';
import { ImportModeSchema, ImportSourceSchema, ImportStatusSchema } from './enums.js';

export const ImportRowErrorSchema = z.object({
  row: z.number().int(),
  path: z.string().optional(),
  code: z.string().optional(),
  message: z.string(),
});
export type ImportRowError = z.infer<typeof ImportRowErrorSchema>;

/**
 * Parsed row from a forest parcel import (meta_3.xlsx sheet "2" shape).
 * Fields map 1:1 to the xlsx headers and to the `parcel` table columns.
 */
export const ImportParsedRowSchema = z.object({
  row: z.number().int(),
  projectUcfKey: z.string().nullable(),
  sequenceId: z.number().int().nullable(),
  projectId: z.string().nullable(),
  raion: z.string().nullable(),
  primarie: z.string().nullable(),
  entitateSilvica: z.string().nullable(),
  codProiect: z.string().nullable(),
  suprafata: z.number().nullable(),
  ucf: z.string().nullable(),
  ocolSilvic: z.string().nullable(),
  anul: z.number().int().nullable(),
  geometryType: z.string().nullable(),
  geometry: z.unknown().nullable(),
  valid: z.boolean(),
});
export type ImportParsedRow = z.infer<typeof ImportParsedRowSchema>;

export const ImportParseResponseSchema = z.object({
  batchId: z.string().uuid(),
  sourceType: ImportSourceSchema,
  sheet: z.string().nullable(),
  detectedEncoding: z.enum(['utf-8', 'windows-1251', 'windows-1252']),
  detectedDelimiter: z.enum([',', ';', '\t']).nullable(),
  headers: z.array(z.string()),
  headerMapping: z.record(z.string()),
  preview: z.array(ImportParsedRowSchema),
  validCount: z.number().int(),
  invalidCount: z.number().int(),
  errors: z.array(ImportRowErrorSchema),
});
export type ImportParseResponse = z.infer<typeof ImportParseResponseSchema>;

/**
 * One row's corrections from the shapefile import's editing step. A key
 * left out is unchanged; `null` (or an empty string) clears the field.
 */
export const ShpRowEditSchema = z
  .object({
    raion: z.string().max(100).nullable().optional(),
    primarie: z.string().max(100).nullable().optional(),
    codPrimarie: z.string().max(10).nullable().optional(),
    anul: z.number().int().min(1900).max(2200).nullable().optional(),
    ucf: z.string().max(40).nullable().optional(),
    suprafata: z.number().nonnegative().max(100000).nullable().optional(),
    entitateSilvica: z.string().max(100).nullable().optional(),
    ocolSilvic: z.string().max(100).nullable().optional(),
  })
  .strict();
export type ShpRowEdit = z.infer<typeof ShpRowEditSchema>;

export const ImportCommitInputSchema = z
  .object({
    batchId: z.string().uuid(),
    mode: ImportModeSchema.default('skip_invalid'),
    targetUserId: z.string().uuid().optional(),
    /** Shapefile polygon import only: corrections keyed by row number
     *  (`rawIndex`), from the editing step. */
    edits: z
      .record(z.string().regex(/^\d+$/), ShpRowEditSchema)
      .refine((r) => Object.keys(r).length <= 20_000, 'too many edited rows')
      .optional(),
  })
  .strict();
export type ImportCommitInput = z.infer<typeof ImportCommitInputSchema>;

export const ImportCommitResponseSchema = z.object({
  batchId: z.string().uuid(),
  imported: z.number().int(),
  /** Rows that already existed and were refreshed from this file
   *  (geometry / area changed). Shapefile targets only. */
  updated: z.number().int().optional(),
  /** Rows that already existed and matched this file exactly. */
  unchanged: z.number().int().optional(),
  skipped: z.number().int(),
  errors: z.array(ImportRowErrorSchema),
  durationMs: z.number().int(),
});
export type ImportCommitResponse = z.infer<typeof ImportCommitResponseSchema>;

/**
 * Parsed row from a photo import (meta_3_photo.xlsx shape).
 */
export const ImportPhotoParsedRowSchema = z.object({
  row: z.number().int(),
  fileName: z.string().nullable(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  downloadUrl: z.string().nullable(),
  ucf: z.string().nullable(),
  suprafata: z.number().nullable(),
  raion: z.string().nullable(),
  primarie: z.string().nullable(),
  ocolSilvic: z.string().nullable(),
  entitateSilvica: z.string().nullable(),
  anul: z.number().int().nullable(),
  codProiect: z.string().nullable(),
  /**
   * Optional project funding stream column. Free text since 2026-06-09
   * (project is now a `project_type` reference, not a closed enum); the
   * engine coerces legacy enum codes to the canonical Romanian labels.
   */
  project: z.string().nullable(),
  valid: z.boolean(),
});
export type ImportPhotoParsedRow = z.infer<typeof ImportPhotoParsedRowSchema>;

export const ImportBatchSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  targetUserId: z.string().uuid().nullable(),
  sourceType: ImportSourceSchema,
  originalFilename: z.string(),
  sheetName: z.string().nullable(),
  status: ImportStatusSchema,
  totalRows: z.number().int(),
  importedRows: z.number().int(),
  skippedRows: z.number().int(),
  importTarget: z.string().optional(),
  createdAt: z.string(),
  committedAt: z.string().nullable(),
  expiresAt: z.string(),
});
export type ImportBatchDto = z.infer<typeof ImportBatchSchema>;
